import { useState } from 'react';
import { Archive, ArchiveRestore, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { abnahmeApi, type KriteriumDto, type TestfallDto } from '../../lib/api/abnahme';

interface Entwurf {
  id: number | null;
  titel: string;
  schritte: string;
  erwartung: string;
  kriteriumId: number | null;
}

const LEER: Entwurf = { id: null, titel: '', schritte: '', erwartung: '', kriteriumId: null };

/**
 * Manuelle Testfälle einer Story — was jemand von Hand durchspielt und was dabei
 * herauskommen muss. Ein Fall kann an einem Kriterium hängen; dann zählt sein
 * Ergebnis zur Abdeckung dieses Kriteriums.
 */
export default function StoryTestfaelle({
  storyId,
  testfaelle,
  kriterien,
  darfBearbeiten,
  onGeaendert,
}: {
  storyId: number;
  testfaelle: TestfallDto[];
  kriterien: KriteriumDto[];
  darfBearbeiten: boolean;
  onGeaendert: () => Promise<void> | void;
}) {
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [archivZeigen, setArchivZeigen] = useState(false);

  const sichtbar = testfaelle.filter((fall) => archivZeigen || !fall.archived);
  const archiviert = testfaelle.length - testfaelle.filter((fall) => !fall.archived).length;
  const kriteriumKey = (id: number | null) => kriterien.find((k) => k.id === id)?.key;

  const ausfuehren = async (arbeit: () => Promise<unknown>, meldung: string) => {
    setSpeichert(true);
    try {
      await arbeit();
      toast.success(meldung);
      await onGeaendert();
      return true;
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Das hat nicht geklappt.');
      return false;
    } finally {
      setSpeichert(false);
    }
  };

  const speichern = async () => {
    if (!entwurf) return;
    const daten = {
      titel: entwurf.titel.trim(),
      schritte: entwurf.schritte,
      erwartung: entwurf.erwartung,
      kriteriumId: entwurf.kriteriumId,
    };
    const ok = await ausfuehren(
      () =>
        entwurf.id
          ? abnahmeApi.testfallAendern(entwurf.id, daten)
          : abnahmeApi.testfallAnlegen(storyId, daten),
      entwurf.id ? 'Testfall gespeichert.' : 'Testfall angelegt.',
    );
    if (ok) setEntwurf(null);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Manuelle Testfälle</CardTitle>
          <CardDescription>
            Was sich nur von Hand prüfen lässt. Sie landen in manuellen Testläufen zum Abhaken.
          </CardDescription>
        </div>
        {archiviert > 0 && (
          <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={archivZeigen} onChange={(e) => setArchivZeigen(e.target.checked)} />
            {archiviert} archiviert
          </label>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {sichtbar.length === 0 && !entwurf && (
          <p className="text-sm text-muted-foreground">Noch keine manuellen Testfälle.</p>
        )}
        {sichtbar.map((fall) =>
          entwurf?.id === fall.id ? null : (
            <div key={fall.id} className={`space-y-1 ${fall.archived ? 'opacity-60' : ''}`}>
              <div className="flex items-start gap-2">
                <p className="flex-1 font-medium">
                  {fall.titel}
                  {kriteriumKey(fall.kriteriumId) && (
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {kriteriumKey(fall.kriteriumId)}
                    </span>
                  )}
                </p>
                {darfBearbeiten && (
                  <div className="flex shrink-0 gap-1">
                    {!fall.archived && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Testfall bearbeiten"
                        onClick={() =>
                          setEntwurf({
                            id: fall.id,
                            titel: fall.titel,
                            schritte: fall.schritte,
                            erwartung: fall.erwartung,
                            kriteriumId: fall.kriteriumId,
                          })
                        }
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={speichert}
                      aria-label={fall.archived ? 'Testfall zurückholen' : 'Testfall archivieren'}
                      onClick={() =>
                        void ausfuehren(
                          () => abnahmeApi.testfallAendern(fall.id, { archived: !fall.archived }),
                          fall.archived ? 'Zurückgeholt.' : 'Archiviert.',
                        )
                      }
                    >
                      {fall.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                )}
              </div>
              {fall.schritte && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{fall.schritte}</p>}
              {fall.erwartung && (
                <p className="whitespace-pre-wrap text-sm">
                  <span className="font-medium">Erwartet: </span>
                  {fall.erwartung}
                </p>
              )}
              {fall.herkunft && <p className="text-xs text-muted-foreground">{fall.herkunft}</p>}
            </div>
          ),
        )}

        {entwurf && (
          <div className="space-y-3 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="testfall-titel">Titel</Label>
              <Input
                id="testfall-titel"
                value={entwurf.titel}
                onChange={(e) => setEntwurf({ ...entwurf, titel: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="testfall-schritte">Schritte</Label>
              <Textarea
                id="testfall-schritte"
                rows={3}
                value={entwurf.schritte}
                onChange={(e) => setEntwurf({ ...entwurf, schritte: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="testfall-erwartung">Erwartung</Label>
              <Textarea
                id="testfall-erwartung"
                rows={2}
                value={entwurf.erwartung}
                onChange={(e) => setEntwurf({ ...entwurf, erwartung: e.target.value })}
              />
            </div>
            {kriterien.filter((k) => !k.archived).length > 0 && (
              <div className="space-y-1">
                <Label htmlFor="testfall-kriterium">Prüft Kriterium</Label>
                <select
                  id="testfall-kriterium"
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  value={entwurf.kriteriumId ?? ''}
                  onChange={(e) =>
                    setEntwurf({ ...entwurf, kriteriumId: e.target.value ? Number(e.target.value) : null })
                  }
                >
                  <option value="">— die ganze Story —</option>
                  {kriterien
                    .filter((k) => !k.archived)
                    .map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.key} · {k.text.slice(0, 80)}
                      </option>
                    ))}
                </select>
              </div>
            )}
            <div className="flex gap-2">
              <Button disabled={speichert || !entwurf.titel.trim()} onClick={() => void speichern()}>
                Speichern
              </Button>
              <Button variant="ghost" onClick={() => setEntwurf(null)}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}

        {darfBearbeiten && !entwurf && (
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setEntwurf({ ...LEER })}>
            <Plus className="h-3.5 w-3.5" /> Testfall
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
